import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import ProductCard from "../components/ProductCard";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useLocation, useSearchParams } from "react-router-dom";
import "../css/Products.css";
import { CacheManager, CACHE_KEYS } from "../utils/cache";

// صفحة المنتجات تحتوي على البحث، الترتيب، والتصنيف
function Products() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [showOnlyOnSale, setShowOnlyOnSale] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [categoriesToShow] = useState(5); // Show 5 categories initially
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8); // 8 products per page
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [priceRange, setPriceRange] = useState([0, 1000]); // Default price range
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(1000);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // جلب الفئات من قاعدة البيانات
  useEffect(() => {
    async function fetchCategories() {
      try {
        // Check cache first
        const cachedCategories = CacheManager.get(CACHE_KEYS.CATEGORIES);
        if (cachedCategories) {
          setCategories(cachedCategories);
          return;
        }

        const snapshot = await getDocs(collection(db, "categories"));
        const data = [];
        snapshot.forEach((doc) => data.push({ id: doc.id, ...doc.data() }));

        // Cache the full data
        CacheManager.set(CACHE_KEYS.CATEGORIES, data, 10 * 60 * 1000); // 10 minutes

        setCategories(data);
      } catch (error) {
        // بيانات تجريبية في حال عدم القدرة على جلب البيانات
        setCategories([
          { id: "1", name: "الجسم", subcategories: [] },
          { id: "2", name: "الوجه", subcategories: [] },
          { id: "3", name: "الشعر", subcategories: [] },
        ]);
      }
    }
    fetchCategories();
  }, []);

  // جلب العلامات التجارية من قاعدة البيانات
  useEffect(() => {
    async function fetchBrands() {
      try {
        // Check cache first
        const cachedBrands = CacheManager.get(CACHE_KEYS.BRANDS);
        if (cachedBrands) {
          setBrands(cachedBrands); // Store full brand objects with id and name
          return;
        }

        const snapshot = await getDocs(collection(db, "brands"));
        const data = [];
        snapshot.forEach((doc) => data.push({ id: doc.id, ...doc.data() }));

        // Cache the full data
        CacheManager.set(CACHE_KEYS.BRANDS, data, 10 * 60 * 1000); // 10 minutes

        setBrands(data); // Store full brand objects
      } catch (error) {
        // بيانات تجريبية في حال عدم القدرة على جلب البيانات
        setBrands([
          { id: "brand1", name: "Zara luxury" },
          { id: "brand2", name: "Nivea" },
          { id: "brand3", name: "L'Oréal" },
          { id: "brand4", name: "Garnier" },
          { id: "brand5", name: "The Ordinary" },
        ]);
      }
    }
    fetchBrands();
  }, []);

  // جلب المنتجات من قاعدة البيانات
  useEffect(() => {
    async function fetchProducts() {
      setLoading(true);
      setError("");
      try {
        // Check cache first
        const cachedProducts = CacheManager.get(CACHE_KEYS.PRODUCTS);
        if (cachedProducts) {
          setProducts(cachedProducts);
          setLoading(false);
          return;
        }

        const querySnapshot = await getDocs(collection(db, "products"));
        const data = [];
        querySnapshot.forEach((doc) => {
          data.push({ id: doc.id, ...doc.data() });
        });

        // Cache products for 5 minutes
        CacheManager.set(CACHE_KEYS.PRODUCTS, data, 5 * 60 * 1000);

        setProducts(data);

        // Calculate min and max prices from products
        if (data.length > 0) {
          const prices = data.map((p) => p.price || 0);
          const min = Math.floor(Math.min(...prices));
          const max = Math.ceil(Math.max(...prices));
          setMinPrice(min);
          setMaxPrice(max);
          setPriceRange([min, max]);
        }
      } catch (error) {
        setError("حدث خطأ في تحميل المنتجات");
        // بيانات تجريبية في حال عدم القدرة على جلب البيانات
        setProducts([
          {
            id: "1",
            name: "كريم مرطب للوجه",
            price: 50,
            brand: "Nivea",
            description:
              "كريم يرطب البشرة ويمنحها نعومة فائقة مع الحماية اليومية.",
            images: ["/images/sample1.jpg"],
            categories: ["الوجه"],
            stock: 15,
          },
          {
            id: "2",
            name: "زيت الأرغان للشعر",
            price: 70,
            brand: "The Ordinary",
            description: "زيت طبيعي 100% لتقوية الشعر وإضافة اللمعان الطبيعي.",
            images: ["/images/sample2.jpg"],
            categories: ["الشعر"],
            stock: 8,
          },
          {
            id: "3",
            name: "غسول الجسم المغذي",
            price: 40,
            brand: "Garnier",
            description: "غسول رائع لتنظيف الجسم برائحة عطرة ومكونات طبيعية.",
            images: ["/images/sample3.jpg"],
            categories: ["الجسم"],
            stock: 0,
          },
          {
            id: "4",
            name: "سيروم فيتامين سي",
            price: 85,
            brand: "The Ordinary",
            description:
              "سيروم مضاد للأكسدة لإشراق البشرة ومحاربة علامات التقدم.",
            images: ["/images/sample4.jpg"],
            categories: ["الوجه"],
            stock: 3,
          },
          {
            id: "5",
            name: "شامبو للشعر الجاف",
            price: 45,
            brand: "L'Oréal",
            description: "شامبو مخصص للشعر الجاف والمتضرر بتركيبة مرطبة عميقة.",
            images: ["/images/sample5.jpg"],
            categories: ["الشعر"],
            stock: 12,
          },
          {
            id: "6",
            name: "كريم مقشر للجسم",
            price: 55,
            brand: "Zara luxury",
            description:
              "مقشر لطيف لإزالة خلايا الجلد الميتة وتجديد نعومة البشرة.",
            images: ["/images/sample6.jpg"],
            categories: ["الجسم"],
            stock: 7,
          },
        ]);
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);

  // تحديث قائمة المنتجات عند تغيّر معايير البحث أو الترتيب أو التصنيف
  useEffect(() => {
    let updated = [...products];

    // البحث بالاسم والوصف
    if (searchTerm) {
      updated = updated.filter((item) => {
        const matchesName = item.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
        const matchesDescription = item.description
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

        // For brand search, look up brand name from brandId
        let matchesBrand = false;
        if (item.brandId) {
          const brand = brands.find((b) => b.id === item.brandId);
          matchesBrand =
            brand &&
            brand.name.toLowerCase().includes(searchTerm.toLowerCase());
        } else if (item.brand) {
          // Backward compatibility for old products with brand name
          matchesBrand = item.brand
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        }

        return matchesName || matchesDescription || matchesBrand;
      });
    }

    // تصفية حسب الفئة الفرعية (له الأولوية)
    if (selectedSubcategory) {
      updated = updated.filter((item) => {
        // Handle both old format (subcategories array) and new format (subcategoryIds array)
        const subcategoryIds = item.subcategoryIds || [];
        const oldSubcategories = item.subcategories || [];

        // Check new format: subcategoryId format is "categoryId_subcategoryName"
        const matchesNewFormat = subcategoryIds.some((subId) =>
          subId.endsWith(`_${selectedSubcategory}`),
        );

        // Check old format
        const matchesOldFormat = oldSubcategories.includes(selectedSubcategory);

        return matchesNewFormat || matchesOldFormat;
      });
    } else if (selectedCategory) {
      // تصفية حسب الفئة الرئيسية فقط إذا لم يتم اختيار فئة فرعية
      updated = updated.filter((item) => {
        // Handle both old format (categories array with names) and new format (categoryIds array)
        const categoryIds = item.categoryIds || [];
        const oldCategories = item.categories || [];

        // Find category ID from name for new format
        const category = categories.find((c) => c.name === selectedCategory);
        const matchesNewFormat = category && categoryIds.includes(category.id);

        // Check old format
        const matchesOldFormat = oldCategories.includes(selectedCategory);

        return matchesNewFormat || matchesOldFormat;
      });
    }

    // تصفية حسب العلامة التجارية
    if (selectedBrand) {
      updated = updated.filter((item) => {
        // Support new format (brandId) and old format (brand name)
        return item.brandId === selectedBrand || item.brand === selectedBrand;
      });
    }

    // تصفية حسب العروض (المنتجات التي بها خصم)
    if (showOnlyOnSale) {
      updated = updated.filter((item) => item.hasDiscount === true);
    }

    // تصفية حسب نطاق السعر
    updated = updated.filter((item) => {
      const price = item.price || 0;
      return price >= priceRange[0] && price <= priceRange[1];
    });

    // الترتيب
    if (sortOrder === "name") {
      updated.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortOrder === "priceAsc") {
      updated.sort((a, b) => a.price - b.price);
    } else if (sortOrder === "priceDesc") {
      updated.sort((a, b) => b.price - a.price);
    } else if (sortOrder === "brand") {
      updated.sort((a, b) => (a.brand || "").localeCompare(b.brand || ""));
    }

    setFilteredProducts(updated);
    setCurrentPage(1); // Reset to first page when filtering
  }, [
    products,
    searchTerm,
    sortOrder,
    selectedCategory,
    selectedSubcategory,
    selectedBrand,
    showOnlyOnSale,
    priceRange,
  ]);

  // Handle URL parameters for brand filtering
  useEffect(() => {
    const brandFromURL = searchParams.get("brand");
    if (brandFromURL) {
      setSelectedBrand(decodeURIComponent(brandFromURL));
    }
  }, [searchParams]);

  // Handle URL parameters for category filtering
  useEffect(() => {
    const categoryFromURL = searchParams.get("category");
    if (categoryFromURL) {
      const decodedCategory = decodeURIComponent(categoryFromURL);
      setSelectedCategory(decodedCategory);
      // Auto-expand if it has subcategories
      const category = categories.find((c) => c.name === decodedCategory);
      if (
        category &&
        category.subcategories &&
        category.subcategories.length > 0
      ) {
        const newExpanded = new Set(expandedCategories);
        newExpanded.add(decodedCategory);
        setExpandedCategories(newExpanded);
      }
    }
  }, [searchParams, categories]);

  // Handle scroll to adjust sidebar position when top bar disappears
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // حساب الصفحات
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSortOrder("");
    setSelectedCategory("");
    setSelectedSubcategory("");
    setSelectedBrand("");
    setShowOnlyOnSale(false);
    setPriceRange([minPrice, maxPrice]);
    setIsMobileSidebarOpen(false);
    // Clear URL parameters
    setSearchParams({});
  };

  const toggleCategory = (categoryName) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryName)) {
      newExpanded.delete(categoryName);
    } else {
      newExpanded.add(categoryName);
    }
    setExpandedCategories(newExpanded);
  };

  const handleCategoryClick = (categoryName) => {
    if (selectedCategory === categoryName) {
      setSelectedCategory("");
      setSelectedSubcategory("");
    } else {
      setSelectedCategory(categoryName);
      setSelectedSubcategory("");
      // Auto-expand when selecting
      const newExpanded = new Set(expandedCategories);
      newExpanded.add(categoryName);
      setExpandedCategories(newExpanded);
    }
  };

  const handleSubcategoryClick = (subcategoryName) => {
    if (selectedSubcategory === subcategoryName) {
      setSelectedSubcategory("");
    } else {
      setSelectedSubcategory(subcategoryName);
    }
  };

  // مكون الهيكل العظمي للتحميل
  const LoadingSkeleton = () => (
    <div className="pr-grid">
      {[...Array(8)].map((_, index) => (
        <div key={index} className="pr-skeleton">
          <div className="pr-skel-image"></div>
          <div className="pr-skel-content">
            <div className="pr-skel-title"></div>
            <div className="pr-skel-desc"></div>
            <div className="pr-skel-price"></div>
            <div className="pr-skel-btn"></div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <Navbar />
      {/* Hero Section */}
      <section className="products-hero">
        <div className="products-hero-overlay"></div>
        <div className="products-hero-content">
          <h1 className="products-hero-title">منتجاتنا</h1>
          <p className="products-hero-subtitle">اكتشفي مجموعتنا الفاخرة</p>
        </div>
      </section>

      <div className="products-page">
        <div className="pr-container">
          {error && (
            <div className="pr-error" role="alert">
              <span className="pr-error-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </span>
              <span>{error}</span>
              <button
                onClick={() => window.location.reload()}
                className="pr-retry-btn"
              >
                إعادة المحاولة
              </button>
            </div>
          )}

          {/* Search Bar */}
          <div className="pr-search-bar">
            <div className="pr-search-wrapper">
              <button
                className="pr-filter-btn"
                onClick={() => setIsMobileSidebarOpen(true)}
                aria-label="فتح الفلاتر"
              >
                <i className="fas fa-sliders-h"></i>
                <span>الفلاتر</span>
              </button>

              <i className="fas fa-search pr-search-icon"></i>
              <input
                id="search-input"
                type="text"
                placeholder="ابحث عن منتج أو وصف أو علامة تجارية..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pr-search-input"
                aria-label="البحث في المنتجات"
              />
              {searchTerm && (
                <button
                  className="pr-clear-search"
                  onClick={() => setSearchTerm("")}
                  aria-label="مسح البحث"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}

              <select
                id="sort-select"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="pr-sort-dropdown"
                aria-label="ترتيب المنتجات"
              >
                <option value="">الترتيب</option>
                <option value="name">الاسم (أ-ي)</option>
                <option value="brand">العلامة التجارية (أ-ي)</option>
                <option value="priceAsc">السعر (الأقل أولاً)</option>
                <option value="priceDesc">السعر (الأعلى أولاً)</option>
              </select>
            </div>
          </div>

          {/* Active Filters Tags */}
          {/* {(searchTerm ||
            sortOrder ||
            selectedCategory ||
            selectedSubcategory ||
            selectedBrand ||
            showOnlyOnSale ||
            (priceRange[0] !== minPrice || priceRange[1] !== maxPrice)) && (
            <div className="pr-active-filters">
              <span className="pr-active-label">
                <i className="fas fa-filter"></i>
                الفلاتر المطبقة:
              </span>
              <div className="pr-filter-tags">
                {searchTerm && (
                  <span className="pr-filter-tag">
                    <i className="fas fa-search"></i>
                    {searchTerm}
                    <button onClick={() => setSearchTerm("")}>×</button>
                  </span>
                )}
                {selectedCategory && (
                  <span className="pr-filter-tag">
                    <i className="fas fa-tag"></i>
                    {selectedCategory}
                    <button
                      onClick={() => {
                        setSelectedCategory("");
                        setSelectedSubcategory("");
                      }}
                    >
                      ×
                    </button>
                  </span>
                )}
                {selectedSubcategory && (
                  <span className="pr-filter-tag pr-filter-tag-subcategory">
                    <i className="fas fa-arrow-left"></i>
                    {selectedSubcategory}
                    <button onClick={() => setSelectedSubcategory("")}>
                      ×
                    </button>
                  </span>
                )}
                {selectedBrand && (
                  <span className="pr-filter-tag">
                    <i className="fas fa-award"></i>
                    {brands.find((b) => b.id === selectedBrand)?.name ||
                      selectedBrand}
                    <button onClick={() => setSelectedBrand("")}>×</button>
                  </span>
                )}
                {showOnlyOnSale && (
                  <span className="pr-filter-tag">
                    <i className="fas fa-tag"></i>
                    عروض فقط
                    <button onClick={() => setShowOnlyOnSale(false)}>×</button>
                  </span>
                )}
                {(priceRange[0] !== minPrice || priceRange[1] !== maxPrice) && (
                  <span className="pr-filter-tag">
                    <i className="fas fa-dollar-sign"></i>
                    ₪{priceRange[0]} - ₪{priceRange[1]}
                    <button onClick={() => setPriceRange([minPrice, maxPrice])}>×</button>
                  </span>
                )}
              </div>
              <button className="pr-clear-all-btn" onClick={clearFilters}>
                <i className="fas fa-times-circle"></i>
                مسح الكل
              </button>
            </div>
          )} */}

          {/* Main Content: Sidebar + Products */}
          <div className="pr-main-content">
            {/* Mobile Floating Filter Button */}
            <button
              className="pr-mobile-floating-filter"
              onClick={() => setIsMobileSidebarOpen(true)}
              aria-label="فتح التصفية"
            >
              <i className="fas fa-sliders-h"></i>
            </button>

            {/* Backdrop Overlay */}
            {isMobileSidebarOpen && (
              <div
                className="pr-sidebar-backdrop"
                onClick={() => setIsMobileSidebarOpen(false)}
              ></div>
            )}

            {/* Sidebar Filters */}
            <aside
              className={`pr-sidebar ${isMobileSidebarOpen ? "pr-sidebar-mobile-open" : ""} ${isScrolled ? "pr-sidebar-scrolled" : ""}`}
            >
              <div className="pr-sidebar-header">
                <div className="pr-sidebar-title">
                  <i className="fas fa-sliders-h"></i>
                  <h3>التصفية</h3>
                </div>
                <button
                  className="pr-sidebar-close"
                  onClick={() => setIsMobileSidebarOpen(false)}
                  aria-label="إغلاق الفلاتر"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              {/* Price Range Filter */}
              <div className="pr-filter-group">
                <label className="pr-filter-label">
                  <i className="fas fa-dollar-sign"></i>
                  نطاق السعر
                </label>
                <div className="pr-price-range-container">
                  <div className="pr-price-range-values">
                    <span className="pr-price-value">₪{priceRange[0]}</span>
                    <span className="pr-price-separator">-</span>
                    <span className="pr-price-value">₪{priceRange[1]}</span>
                  </div>
                  <div className="pr-price-slider-wrapper">
                    <div className="pr-price-slider-track"></div>
                    <div
                      className="pr-price-slider-fill"
                      style={{
                        right: `${((priceRange[0] - minPrice) / (maxPrice - minPrice)) * 100}%`,
                        width: `${((priceRange[1] - priceRange[0]) / (maxPrice - minPrice)) * 100}%`,
                      }}
                    ></div>
                    <input
                      type="range"
                      min={minPrice}
                      max={maxPrice}
                      value={priceRange[0]}
                      onChange={(e) => {
                        const newMin = Number(e.target.value);
                        if (newMin <= priceRange[1]) {
                          setPriceRange([newMin, priceRange[1]]);
                        }
                      }}
                      className="pr-price-slider pr-price-slider-min"
                      aria-label="الحد الأدنى للسعر"
                    />
                    <input
                      type="range"
                      min={minPrice}
                      max={maxPrice}
                      value={priceRange[1]}
                      onChange={(e) => {
                        const newMax = Number(e.target.value);
                        if (newMax >= priceRange[0]) {
                          setPriceRange([priceRange[0], newMax]);
                        }
                      }}
                      className="pr-price-slider pr-price-slider-max"
                      aria-label="الحد الأقصى للسعر"
                    />
                  </div>
                  <div className="pr-price-inputs">
                    <input
                      type="number"
                      min={minPrice}
                      max={priceRange[1]}
                      value={priceRange[0]}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") return;
                        const newMin = Number(value);
                        if (newMin >= minPrice && newMin <= priceRange[1]) {
                          setPriceRange([newMin, priceRange[1]]);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value === "" || Number(value) < minPrice) {
                          setPriceRange([minPrice, priceRange[1]]);
                        } else if (Number(value) > priceRange[1]) {
                          setPriceRange([priceRange[1], priceRange[1]]);
                        }
                      }}
                      className="pr-price-input"
                      placeholder="من"
                      aria-label="أدخل الحد الأدنى للسعر"
                    />
                    <span className="pr-price-input-separator">-</span>
                    <input
                      type="number"
                      min={priceRange[0]}
                      max={maxPrice}
                      value={priceRange[1]}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") return;
                        const newMax = Number(value);
                        if (newMax >= priceRange[0] && newMax <= maxPrice) {
                          setPriceRange([priceRange[0], newMax]);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value === "" || Number(value) > maxPrice) {
                          setPriceRange([priceRange[0], maxPrice]);
                        } else if (Number(value) < priceRange[0]) {
                          setPriceRange([priceRange[0], priceRange[0]]);
                        }
                      }}
                      className="pr-price-input"
                      placeholder="إلى"
                      aria-label="أدخل الحد الأقصى للسعر"
                    />
                  </div>
                </div>
              </div>

              <div className="pr-categories-filter">
                <label className="pr-filter-label">
                  <i className="fas fa-th-large"></i>
                  الفئات
                </label>
                <div className="pr-category-accordion">
                  <button
                    className={`pr-category-item ${!selectedCategory && !selectedSubcategory ? "active" : ""}`}
                    onClick={() => handleCategoryClick("")}
                  >
                    <span className="pr-category-icon">
                      <i className="fas fa-th"></i>
                    </span>
                    <span className="pr-category-name">الكل</span>
                  </button>
                  {(showAllCategories
                    ? categories
                    : categories.slice(0, categoriesToShow)
                  ).map((cat) => {
                    const catName = cat.name;
                    const hasSubcategories =
                      cat.subcategories && cat.subcategories.length > 0;
                    const isExpanded = expandedCategories.has(catName);
                    const isCategorySelected = selectedCategory === catName;

                    return (
                      <div key={catName} className="pr-category-wrapper">
                        <button
                          className={`pr-category-item ${isCategorySelected && !selectedSubcategory ? "active" : ""}`}
                          onClick={() => handleCategoryClick(catName)}
                        >
                          <span className="pr-category-icon">
                            {catName === "الوجه" ? (
                              <i className="fas fa-face-smile"></i>
                            ) : catName === "الشعر" ? (
                              <i className="fas fa-scissors"></i>
                            ) : catName === "الجسم" ? (
                              <i className="fas fa-pump-soap"></i>
                            ) : (
                              <i className="fas fa-tag"></i>
                            )}
                          </span>
                          <span className="pr-category-name">{catName}</span>
                          {hasSubcategories && (
                            <button
                              className="pr-category-expand"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCategory(catName);
                              }}
                            >
                              <i
                                className={`fas fa-chevron-${isExpanded ? "up" : "down"}`}
                              ></i>
                            </button>
                          )}
                        </button>
                        {hasSubcategories && isExpanded && (
                          <div className="pr-subcategories-list">
                            {cat.subcategories.map((sub) => (
                              <button
                                key={sub}
                                className={`pr-subcategory-item ${selectedSubcategory === sub ? "active" : ""}`}
                                onClick={() => {
                                  // Select parent category if not selected
                                  if (selectedCategory !== catName) {
                                    setSelectedCategory(catName);
                                  }
                                  handleSubcategoryClick(sub);
                                }}
                              >
                                <span className="pr-subcategory-dot"></span>
                                <span className="pr-subcategory-name">
                                  {sub}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {categories.length > categoriesToShow && (
                  <button
                    className="pr-show-more-categories"
                    onClick={() => setShowAllCategories(!showAllCategories)}
                  >
                    {showAllCategories ? (
                      <>
                        <i className="fas fa-chevron-up"></i>
                        <span>عرض أقل</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-chevron-down"></i>
                        <span>
                          عرض المزيد ({categories.length - categoriesToShow})
                        </span>
                      </>
                    )}
                  </button>
                )}
              </div>

              <div className="pr-brands-filter">
                <label className="pr-filter-label">
                  <i className="fas fa-award"></i>
                  العلامات التجارية
                </label>
                <div className="pr-brand-chips">
                  <button
                    className={`pr-brand-chip ${!selectedBrand ? "active" : ""}`}
                    onClick={() => setSelectedBrand("")}
                  >
                    <span className="pr-chip-icon">
                      <i className="fas fa-th"></i>
                    </span>
                    <span className="pr-chip-text">الكل</span>
                  </button>
                  {brands.map((brand) => (
                    <button
                      key={brand.id}
                      className={`pr-brand-chip ${selectedBrand === brand.id ? "active" : ""}`}
                      onClick={() => setSelectedBrand(brand.id)}
                    >
                      <span className="pr-chip-icon">
                        <i className="fas fa-award"></i>
                      </span>
                      <span className="pr-chip-text">{brand.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="pr-filter-group">
                <label className="pr-filter-label">
                  <i className="fas fa-tag"></i>
                  العروض
                </label>
                <div className="pr-checkbox-wrapper">
                  <input
                    type="checkbox"
                    id="sale-checkbox"
                    checked={showOnlyOnSale}
                    onChange={(e) => setShowOnlyOnSale(e.target.checked)}
                    className="pr-filter-checkbox"
                    aria-label="إظهار المنتجات في العروض فقط"
                  />
                  <label htmlFor="sale-checkbox" className="pr-checkbox-label">
                    إظهار المنتجات في العروض فقط
                  </label>
                </div>
              </div>

              {(sortOrder ||
                selectedCategory ||
                selectedBrand ||
                showOnlyOnSale) && (
                <button className="pr-reset-filters" onClick={clearFilters}>
                  <i className="fas fa-redo"></i>
                  إعادة تعيين الفلاتر
                </button>
              )}
            </aside>

            {/* Products Content */}
            <div className="pr-content">
              {loading ? (
                <LoadingSkeleton />
              ) : filteredProducts.length === 0 ? (
                <div className="pr-empty">
                  <div className="pr-empty-icon">
                    <i className="fas fa-box-open"></i>
                  </div>
                  <h3>لا توجد منتجات</h3>
                  <p>
                    {searchTerm || selectedCategory || selectedBrand
                      ? "لا توجد منتجات تطابق معايير البحث المحددة"
                      : "لا توجد منتجات متاحة حالياً"}
                  </p>
                  {(searchTerm ||
                    selectedCategory ||
                    sortOrder ||
                    selectedBrand) && (
                    <button
                      className="pr-clear-filters-btn"
                      onClick={clearFilters}
                    >
                      <i className="fas fa-times-circle"></i>
                      مسح جميع الفلاتر
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="pr-grid">
                    {currentItems.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="pr-pagination">
                      <button
                        className="pr-page-btn prev"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        aria-label="الصفحة السابقة"
                      >
                        <i className="fas fa-chevron-right"></i>
                        السابق
                      </button>

                      <div className="pr-page-numbers">
                        {[...Array(totalPages)].map((_, index) => {
                          const pageNumber = index + 1;

                          // Show first page, last page, current page, and adjacent pages
                          if (
                            pageNumber === 1 ||
                            pageNumber === totalPages ||
                            (pageNumber >= currentPage - 1 &&
                              pageNumber <= currentPage + 1)
                          ) {
                            return (
                              <button
                                key={pageNumber}
                                className={`pr-page-btn ${
                                  currentPage === pageNumber ? "active" : ""
                                }`}
                                onClick={() => handlePageChange(pageNumber)}
                                aria-label={`الصفحة ${pageNumber}`}
                                aria-current={
                                  currentPage === pageNumber
                                    ? "page"
                                    : undefined
                                }
                              >
                                {pageNumber}
                              </button>
                            );
                          } else if (
                            pageNumber === currentPage - 2 ||
                            pageNumber === currentPage + 2
                          ) {
                            return (
                              <span
                                key={pageNumber}
                                className="pr-page-ellipsis"
                              >
                                ...
                              </span>
                            );
                          }
                          return null;
                        })}
                      </div>

                      <button
                        className="pr-page-btn next"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        aria-label="الصفحة التالية"
                      >
                        التالي
                        <i className="fas fa-chevron-left"></i>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default Products;
